import { useCategory } from '../hooks/useCategory'
import { useCrop } from '../hooks/useCrop'
import {
	Box,
	Button,
	Typography,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Divider,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Alert
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import useRole from '../hooks/useRole'

export const ManageData = () => {
	const { canWrite } = useRole()
	
	// Hooks para categorías y cultivos
	const {
		categories,
		createCategory,
		updateCategory,
		deleteCategory,
		loadingList: loadingCategories
	} = useCategory()

	const {
		crops,
		createCrop,
		addGermplasm,
		removeGermplasm,
		deleteCrop,
		loadingList: loadingCrops
	} = useCrop()

	// Agregar nueva categoría
	const handleAgregarCategoria = async () => {
		const nuevaCategoria = prompt('Ingrese el nombre de la nueva categoría:')
		if (!nuevaCategoria || !nuevaCategoria.trim()) return
		await createCategory({ name: nuevaCategoria, description: 'N/A' })
	}

	// Editar categoría
	const handleEditarCategoria = async (id: string) => {
		const nuevoNombre = prompt('Ingrese el nuevo nombre de la categoría:')
		if (!nuevoNombre || !nuevoNombre.trim()) return
		await updateCategory(id, { name: nuevoNombre })
	}

	// Eliminar categoría
	const handleEliminarCategoria = async (id: string) => {
		if (window.confirm('¿Está seguro de eliminar esta categoría?')) {
			await deleteCategory(id)
		}
	}

	// Agregar nuevo cultivo
	const handleAgregarCultivo = async () => {
		const nuevoCultivo = prompt('Ingrese el nombre del nuevo cultivo:')
		if (!nuevoCultivo || !nuevoCultivo.trim()) return
		await createCrop({ name: nuevoCultivo })
	}

	// Agregar germoplasma a un cultivo
	const handleAgregarGermoplasma = async (cropId: string) => {
		const nuevoGermoplasma = prompt('Ingrese el nombre del nuevo germoplasma:')
		if (!nuevoGermoplasma || !nuevoGermoplasma.trim()) return
		await addGermplasm(cropId, { name: nuevoGermoplasma })
	}

	// Eliminar germoplasma de un cultivo
	const handleEliminarGermoplasma = async (cropId: string, germoplasmaName: string) => {
		if (window.confirm('¿Está seguro de eliminar este germoplasma?')) {
			await removeGermplasm(cropId, { name: germoplasmaName })
		}
	}

	// Eliminar cultivo
	const handleEliminarCultivo = async (cropId: string) => {
		if (window.confirm('¿Está seguro de eliminar este cultivo?')) {
			await deleteCrop(cropId)
		}
	}

	return (
		<Box p={3}>
			{!canWrite() && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					No tienes permisos para modificar datos. Solo puedes ver la información existente.
				</Alert>
			)}
			
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={5} mt={3}>
				<Typography variant="h4">Manejo de Datos</Typography>
				<Button variant="contained" disabled={!canWrite()}>Carga Masiva</Button>
			</Box>
			<Divider sx={{ my: 2 }} />

			{/* Sección de Categorías globales */}
			<Typography variant="h5" gutterBottom>
				Categorías Globales
			</Typography>
			{loadingCategories ? (
				<Typography>Cargando categorías...</Typography>
			) : (
				<>
					<List>
						{categories.map((cat) => (
							<ListItem key={cat.uid} disableGutters>
								<ListItemText primary={cat.name} />
								{canWrite() && (
									<>
										<IconButton size="small" onClick={() => handleEditarCategoria(cat.uid)}>
											<EditIcon fontSize="small" />
										</IconButton>
										<IconButton size="small" onClick={() => handleEliminarCategoria(cat.uid)}>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</>
								)}
							</ListItem>
						))}
					</List>
					<Button variant="outlined" onClick={handleAgregarCategoria} sx={{ mb: 2 }} disabled={!canWrite()}>
						Agregar Categoría
					</Button>
				</>
			)}

			<Divider sx={{ my: 4 }} />

			{/* Sección de Cultivos con colapsable para germoplasma */}
			<Typography variant="h5" gutterBottom>
				Cultivos
			</Typography>
			{loadingCrops ? (
				<Typography>Cargando cultivos...</Typography>
			) : (
				crops.map((cultivo) => (
					<Accordion key={cultivo.uid} sx={{ mb: 2 }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls={`panel-${cultivo.uid}-content`}
							id={`panel-${cultivo.uid}-header`}
						>
							<Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
								<Typography variant="h6" sx={{ flexGrow: 1 }}>
									{cultivo.name}
								</Typography>
								{canWrite() && (
									<IconButton size="small" onClick={() => handleEliminarCultivo(cultivo.uid)}>
										<DeleteIcon fontSize="small" />
									</IconButton>
								)}
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12 }}>
									<Typography variant="h6" textAlign="center">
										Germoplasma
									</Typography>
									<List>
										{cultivo.germplasm.map((germoplasma) => (
											<ListItem key={germoplasma._id}>
												<ListItemText primary={germoplasma.name} />
												{canWrite() && (
													<IconButton
														size="small"
														onClick={() => handleEliminarGermoplasma(cultivo.uid, germoplasma.name)}
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												)}
											</ListItem>
										))}
									</List>
									<Button
										variant="outlined"
										sx={{ mt: 2 }}
										onClick={() => handleAgregarGermoplasma(cultivo.uid)}
										disabled={!canWrite()}
									>
										Agregar Germoplasma
									</Button>
								</Grid>
							</Grid>
						</AccordionDetails>
					</Accordion>
				))
			)}

			<Box textAlign="center" mt={3}>
				<Button variant="outlined" onClick={handleAgregarCultivo} disabled={!canWrite()}>
					Agregar Cultivo
				</Button>
			</Box>
		</Box>
	)
}
